'use client';

import { c as _c } from "react/compiler-runtime";
import { useI18n } from '~/features/i18n';
import { Icon } from '~/components/primitives';
import { Units } from '~/modules/units';
import { cn } from '~/utils';

/** The bar is this tall; `globals.css` grows the header height by the same
 * amount while the bar is shown. */
export const USP_TOP_BAR_HEIGHT_CLASS = {
  bar: 'h-10'
} as const;
/** `h-10` in pixels; the header slides up by this much once the bar is scrolled past. */
export const USP_TOP_BAR_HEIGHT_PX = 40;
export type UspTopBarData = {
  activityCount: number;
  averageScore: number | null;
  totalReviews: number | null;
  phoneNumber: string | null;
};

/** Funkey's three promises in one line above the site header, plus a phone
 * number on the right. They stand still: a line that rotates or carries a
 * review score next to a catalogue count takes a second read, and this bar
 * only has a glance. A phone is too narrow for three, so the row scrolls
 * sideways there. */
export const UspTopBar: React.FC<UspTopBarData> = props => {
  const $ = _c(39);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n.locale || $[1] !== props.activityCount) {
    t0 = Units.formatNumberWithThousandSeparator(props.activityCount, i18n.locale);
    $[0] = i18n.locale;
    $[1] = props.activityCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <strong className="font-bold text-white">{t0}</strong>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== i18n) {
    t2 = i18n._("usp.topbar.activities", undefined, {
      message: "teambuildings"
    });
    $[5] = i18n;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      key: "count",
      label: <>{t1}{" "}{t2}</>
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== i18n) {
    t4 = i18n._("usp.topbar.fullservice", undefined, {
      message: "Everything taken care of"
    });
    $[10] = i18n;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = {
      key: "service",
      label: t4
    };
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== i18n) {
    t6 = i18n._("usp.topbar.response", undefined, {
      message: "A reply within one working day"
    });
    $[14] = i18n;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = {
      key: "response",
      label: t6
    };
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t3 || $[19] !== t5 || $[20] !== t7) {
    t8 = [t3, t5, t7];
    $[18] = t3;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const usps = t8;
  let t10;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("container flex items-center text-sm text-blue-100", USP_TOP_BAR_HEIGHT_CLASS.bar);
    t10 = cn("flex min-w-0 items-center gap-4 lg:gap-6", "max-lg:flex-1 max-lg:overflow-x-auto max-lg:[scrollbar-width:none] max-lg:[&::-webkit-scrollbar]:hidden", "max-lg:[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)]");
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  let t11;
  if ($[24] !== usps) {
    t11 = <ul className={t10}>{usps.map(_temp)}</ul>;
    $[24] = usps;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== i18n || $[27] !== props.averageScore || $[28] !== props.totalReviews) {
    t12 = props.averageScore !== null && props.totalReviews !== null && <span className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap xl:inline-flex"><Icon name="star" className="size-3.5 fill-current text-yellow" aria-hidden={true} /><strong className="font-bold text-white">{props.averageScore.toLocaleString(i18n.locale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        })}</strong>{i18n._("usp.topbar.reviews", {
        count: Units.formatNumberWithThousandSeparator(props.totalReviews, i18n.locale)
      })}</span>;
    $[26] = i18n;
    $[27] = props.averageScore;
    $[28] = props.totalReviews;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== i18n || $[31] !== props.phoneNumber) {
    t13 = props.phoneNumber && <span className="truncate whitespace-nowrap">{i18n._("usp.topbar.phone", undefined, {
        message: "Rather talk to someone?"
      })}{" "}<a href={`tel:${props.phoneNumber.replaceAll(" ", "")}`} className="font-bold text-white hover:underline">{props.phoneNumber}</a></span>;
    $[30] = i18n;
    $[31] = props.phoneNumber;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="ml-auto hidden min-w-0 items-center gap-6 lg:flex">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t11 || $[37] !== t14) {
    t15 = <div data-testid="activity-plp-usp-bar" className={t9}>{t11}{t14}</div>;
    $[36] = t11;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
};
function _temp(usp) {
  return <li key={usp.key} className="inline-flex shrink-0 items-center gap-1.5"><Icon name="check" className="size-3.5 shrink-0 text-turqoise" aria-hidden={true} /><span className="whitespace-nowrap">{usp.label}</span></li>;
}